import { useSyncExternalStore } from 'react';

/**
 * Subscribes to a `window.matchMedia` query and returns the current match state.
 *
 * Uses `useSyncExternalStore` rather than `useEffect` so subscriptions reconcile
 * with React's render cycle and there is no flash on first paint.
 *
 * Server-safe: returns `false` during SSR/hydration when `window` is undefined,
 * which matches our pure-CSR app behavior (no server rendering in this project).
 */
export function useMediaQuery(query: string): boolean {
    return useSyncExternalStore(
        (callback) => {
            if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
                return () => undefined;
            }
            const mql = window.matchMedia(query);
            mql.addEventListener('change', callback);
            return () => mql.removeEventListener('change', callback);
        },
        () => {
            if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
                return false;
            }
            return window.matchMedia(query).matches;
        },
        () => false,
    );
}
